import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Search, Calendar, ArrowRight, Clock, User, 
  ChevronRight, Tag, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const blogPosts = [
  {
    id: 1,
    slug: 'preparing-horse-for-boarding',
    title: 'How to Prepare Your Horse for Boarding: A Complete Guide',
    excerpt: 'Moving your horse to a new boarding facility can be stressful for both you and your equine companion. Here\'s everything you need to know to make the transition smooth.',
    image: 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=600&h=400&fit=crop',
    category: 'Horse Care',
    author: 'Sarah Mitchell',
    date: '2026-01-02',
    readTime: '8 min read',
    featured: true,
  },
  {
    id: 2,
    slug: 'hosting-tips-beginners',
    title: '10 Tips for New Hosts: Starting Your Boarding Business',
    excerpt: 'Thinking about hosting horses on your property? Learn from experienced hosts about what it takes to run a successful boarding operation.',
    image: 'https://images.unsplash.com/photo-1591330189093-f0a4b8c96a79?w=600&h=400&fit=crop',
    category: 'Hosting',
    author: 'James Crawford',
    date: '2025-12-28',
    readTime: '10 min read',
    featured: true,
  },
  {
    id: 3,
    slug: 'cross-country-travel-tips',
    title: 'Cross-Country with Horses: Finding Layover Stops',
    excerpt: 'Planning a long-distance move with your horses? We\'ve compiled tips from experienced travelers on finding safe overnight stops.',
    image: 'https://images.unsplash.com/photo-1634212926530-2cf2ac9e0df9?w=600&h=400&fit=crop',
    category: 'Travel',
    author: 'Emily Rodriguez',
    date: '2025-12-20',
    readTime: '6 min read',
    featured: false,
  },
  {
    id: 4,
    slug: 'winter-boarding-considerations',
    title: 'Winter Boarding: What to Look For in Cold Weather',
    excerpt: 'Cold weather brings unique challenges for horse boarding. Learn what amenities and preparations matter most during winter months.',
    image: 'https://images.unsplash.com/photo-1551053343-65678990726b?w=600&h=400&fit=crop',
    category: 'Horse Care',
    author: 'Sarah Mitchell',
    date: '2025-12-15',
    readTime: '7 min read',
    featured: false,
  },
  {
    id: 5,
    slug: 'pricing-your-listing',
    title: 'How to Price Your Listing: A Host\'s Guide',
    excerpt: 'Setting the right price for your boarding services can be tricky. Here\'s a data-driven approach to competitive pricing.',
    image: 'https://images.unsplash.com/photo-1563116128-261f46267cac?w=600&h=400&fit=crop',
    category: 'Hosting',
    author: 'James Crawford',
    date: '2025-12-10',
    readTime: '5 min read',
    featured: false,
  },
  {
    id: 6,
    slug: 'vaccination-requirements',
    title: 'Understanding Vaccination Requirements for Boarding',
    excerpt: 'Different facilities have different vaccination requirements. Here\'s what you need to know to keep your horse healthy and compliant.',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop',
    category: 'Horse Care',
    author: 'Dr. Amanda Foster',
    date: '2025-12-05',
    readTime: '9 min read',
    featured: false,
  },
];

const categories = ['All', 'Horse Care', 'Hosting', 'Travel', 'Community'];

const BlogCard = ({ post, featured = false }) => (
  <Link
    to={`/blog/${post.slug}`}
    className={`group block bg-farmhouse-paper border border-farmhouse-subtle rounded-md overflow-hidden card-hover ${
      featured ? 'md:flex' : ''
    }`}
    data-testid={`blog-post-${post.id}`}
  >
    <div className={`${featured ? 'md:w-1/2' : ''} aspect-[16/10] overflow-hidden`}>
      <img
        src={post.image}
        alt={post.title}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    </div>
    <div className={`p-6 ${featured ? 'md:w-1/2 md:flex md:flex-col md:justify-center' : ''}`}>
      <div className="flex items-center gap-3 mb-3">
        <Badge className="bg-farmhouse-saddle/20 text-farmhouse-saddle border-0">
          {post.category}
        </Badge>
        <span className="text-farmhouse-muted text-sm flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {post.readTime}
        </span>
      </div>
      <h3 className={`font-serif font-semibold text-farmhouse-cream group-hover:text-farmhouse-saddle transition-colors mb-3 ${
        featured ? 'text-2xl' : 'text-lg'
      }`}>
        {post.title}
      </h3>
      <p className="text-farmhouse-muted text-sm leading-relaxed mb-4 line-clamp-2">
        {post.excerpt}
      </p>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-farmhouse-muted">
          <User className="w-4 h-4" />
          <span>{post.author}</span>
        </div>
        <span className="text-farmhouse-muted text-sm">
          {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
      </div>
    </div>
  </Link>
);

export default function Blog() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredPosts = blogPosts.filter((post) => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPosts = filteredPosts.filter(p => p.featured);
  const regularPosts = filteredPosts.filter(p => !p.featured);

  return (
    <div className="min-h-screen bg-farmhouse-charcoal" data-testid="blog-page">
      {/* Hero */}
      <section className="pt-16 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-farmhouse-saddle tracking-[0.2em] uppercase text-sm font-medium mb-4">
            Our Blog
          </p>
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-farmhouse-cream mb-6">
            Insights & Stories
          </h1>
          <p className="text-xl text-farmhouse-muted max-w-2xl mx-auto mb-8">
            Tips, guides, and stories from the Barns & Pastures community.
          </p>
          
          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-farmhouse-muted" />
            <Input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input pl-12"
              data-testid="blog-search"
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-4 border-y border-farmhouse-subtle bg-farmhouse-paper">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeCategory === category
                    ? 'bg-farmhouse-saddle text-white'
                    : 'text-farmhouse-muted hover:text-farmhouse-cream hover:bg-farmhouse-subtle'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && !searchQuery && activeCategory === 'All' && (
        <section className="py-12 bg-farmhouse-charcoal">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="font-serif text-2xl font-semibold text-farmhouse-cream mb-8">
              Featured Articles
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {featuredPosts.map((post) => (
                <BlogCard key={post.id} post={post} featured />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Posts */}
      <section className="py-12 bg-farmhouse-paper">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-serif text-2xl font-semibold text-farmhouse-cream">
              {searchQuery ? 'Search Results' : activeCategory === 'All' ? 'Latest Articles' : activeCategory}
            </h2>
            <span className="text-farmhouse-muted text-sm">
              {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''}
            </span>
          </div>

          {regularPosts.length > 0 || (searchQuery && featuredPosts.length > 0) ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(searchQuery ? filteredPosts : regularPosts).map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-farmhouse-charcoal border border-farmhouse-subtle rounded-md">
              <Search className="w-12 h-12 mx-auto mb-4 text-farmhouse-subtle" />
              <p className="text-farmhouse-muted">No articles found</p>
              <p className="text-farmhouse-muted text-sm mt-2">
                Try a different search term or category
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-farmhouse-charcoal">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl font-semibold text-farmhouse-cream mb-4">
            Stay Updated
          </h2>
          <p className="text-farmhouse-muted mb-6">
            Get the latest articles and tips delivered to your inbox.
          </p>
          <NewsletterForm />
        </div>
      </section>
    </div>
  );
}

function NewsletterForm() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/newsletter/subscribe', { email });
      if (response.data.status === 'exists') {
        toast.info('You\'re already subscribed!');
      } else {
        toast.success('Successfully subscribed to our newsletter!');
      }
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
      <Input
        type="email"
        placeholder="Enter your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="form-input flex-1"
        data-testid="newsletter-email"
      />
      <Button type="submit" className="btn-primary" disabled={loading} data-testid="newsletter-submit">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <>
            Subscribe
            <ArrowRight className="w-4 h-4 ml-2" />
          </>
        )}
      </Button>
    </form>
  );
}
